import apiSlice from './apiSlice';
import type {
  CasinoGroupedGamesQueryParams,
  CasinoGroupedGamesResponse,
  CasinoGamesQueryParams,
  CasinoGamesResponse,
  CasinoGameLite,
  CasinoGameRowsResponse,
  CasinoFavoriteStatus,
  CasinoTrendingResponse,
  CasinoMostPlayedResponse,
  CasinoProvidersQueryParams,
  CasinoProvidersResponse,
  CreateCasinoSessionRequest,
  CreateCasinoSessionResponse,
} from '@/types/casino';

export const casinoApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCasinoCategories: builder.query<{ games: string[] }, { search?: string; provider?: string }>(
      {
        query: (params) => {
          return {
            url: `/casino/category`,
            method: 'GET',
            params,
          };
        },
        // providesTags: ['CasinoCategories'],
      }
    ),

    getCasinoProviders: builder.query<CasinoProvidersResponse, CasinoProvidersQueryParams | void>({
      query: (params) => {
        return {
          url: `/casino/providers`,
          method: 'GET',
          params: { ...params, status: 'Active' },
        };
      },
      // providesTags: ['CasinoProviders'],
    }),

    getCasinoGames: builder.query<CasinoGamesResponse, CasinoGamesQueryParams | void>({
      query: (params) => ({
        url: '/casino/games',
        method: 'GET',
        params: {
          ...params,
          status: 'ACTIVE',
        },
      }),
    }),

    getCasinoGamesGrouped: builder.query<
      CasinoGroupedGamesResponse,
      CasinoGroupedGamesQueryParams | void
    >({
      query: (params) => ({
        url: '/casino/games/grouped',
        method: 'GET',
        ...(params && { params }),
      }),
    }),

    // "Hot" — the games an admin flagged as trending.
    getCasinoTrending: builder.query<CasinoTrendingResponse, void>({
      query: () => ({ url: '/casino/trending', method: 'GET', params: { status: 'ACTIVE' } }),
    }),

    // "Recommended" — there's no dedicated recommendation endpoint, so this
    // is the platform-wide most-played list instead.
    getCasinoMostPlayed: builder.query<CasinoMostPlayedResponse, void>({
      query: () => ({ url: '/casino/most-played', method: 'GET' }),
    }),

    // The logged-in player's favourites (login required — callers skip it
    // when signed out).
    getCasinoFavorites: builder.query<CasinoGameRowsResponse, void>({
      query: () => ({ url: '/casino/favorite', method: 'GET', params: { limit: 100 } }),
      providesTags: ['CasinoFavorites'],
    }),

    // The logged-in player's recently played games, newest first.
    getCasinoRecent: builder.query<CasinoGameRowsResponse, void>({
      query: () => ({ url: '/casino/recent', method: 'GET' }),
    }),

    toggleCasinoFavorite: builder.mutation<
      unknown,
      { game: CasinoGameLite; status: CasinoFavoriteStatus }
    >({
      query: ({ game, status }) => ({
        url: `/casino/favorite/${game.id}`,
        method: 'PUT',
        body: { status },
      }),
      // Flip the heart instantly; roll back if the request fails.
      async onQueryStarted({ game, status }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          casinoApi.util.updateQueryData('getCasinoFavorites', undefined, (draft) => {
            const rows = draft.games ?? [];
            draft.games =
              status === 'FAVORITE'
                ? [{ id: -game.id, game }, ...rows.filter((row) => row.game.id !== game.id)]
                : rows.filter((row) => row.game.id !== game.id);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    createCasinoSession: builder.mutation<CreateCasinoSessionResponse, CreateCasinoSessionRequest>({
      query: ({ id, platform }) => ({
        url: `/casino/create-session/${id}`,
        method: 'POST',
        body: { platform },
      }),
    }),
  }),
});

export const {
  useGetCasinoCategoriesQuery,

  useGetCasinoProvidersQuery,
  useLazyGetCasinoProvidersQuery,

  useGetCasinoGamesQuery,
  useLazyGetCasinoGamesQuery,
  useGetCasinoGamesGroupedQuery,

  useGetCasinoTrendingQuery,
  useGetCasinoMostPlayedQuery,
  useGetCasinoFavoritesQuery,
  useGetCasinoRecentQuery,
  useToggleCasinoFavoriteMutation,

  useCreateCasinoSessionMutation,
} = casinoApi;
